import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';

import useWindowSize from '@hooks/useWindowSize';

import {
	allFilterPages,
	generatePomListParams,
	pomListFilterFields
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import AddPomInfo from '@features/pom/components/AddPomInfo';
import PomCardView from '@features/pom/components/PomCardView';
import PomListTopFilter from '@features/pom/components/PomListTopFilter';
import TableView from '@features/pom/components/TableView';

import { clearFilterResponse, handleFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDependentFilterList } from '@services/commonService';
import { onDeletePom } from '@services/pomService';

import { getUserInfo, groupByKey, isPageReachBottom } from '@utils/Util';

const PomList = () => {
	const [isOpen, setIsOpen] = useState(false);
	const dispatch = useDispatch();
	const [page, setPage] = useState(0);
	const [isShowFilter, setIsShowFilter] = useState(true);
	const [selectedBrand, setSelectedBrand] = useState([]);
	const [selectedViewType, setSelectedViewType] = useState(
		getUserInfo().systemPreference?.POM_LIST_VIEW_TYPE
	);
	const [allPomList, setAllPomList] = useState(null);
	let filterfields = useSelector((store) => store.filterFieldStore);
	const pomListFromStore = useSelector((store) => store.filterResponseStore);
	const scrollRef = useRef({});
	const { sort = [], ...paramsData } = generatePomListParams(filterfields);
	useWindowSize(() => setIsShowFilter(false));

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		setCurrentState(pomListFromStore);
		setAllPomList(pomListFromStore);
	}, [pomListFromStore]);

	useEffect(() => {
		window.scrollTo(0, 0);
		dispatch(openLoader());
		dispatch(fetchDependentFilterList({ type: 'POM_LIST' }, paramsData));
		setPage(0);
		let queryParams = generatePomListParams(filterfields, 0);
		dispatch(handleFilter('getAllPomList', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	}, [filterfields]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(openLoader());
			let queryParams = generatePomListParams(filterfields, page);
			dispatch(handleFilter('getAllPomList', queryParams, true)).finally(() => {
				dispatch(closeLoader());
			});
		}
	}, [page]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	const onCollspseFilter = () => setIsShowFilter(!isShowFilter);
	const onSetViewSetting = (selected) => {
		setSelectedViewType(selected);
	};

	const onActionSucces = () => {
		let queryParams = generatePomListParams(filterfields, 0);
		dispatch(handleFilter('getAllPomList', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	};

	const onDeletePomFromList = async (pomId) => {
		try {
			dispatch(openLoader());
			const data = await onDeletePom(pomId);
			if (data?.success) {
				toastSuccess(data.message);
				onActionSucces();
			}
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	const renderPomList = () => {
		if (['parentCategory', 'bodyPart', 'position', 'size', 'side'].includes(sort[0])) {
			let groupedProducts = groupByKey(allPomList?.data, `${sort[0]}.name`);
			return Object.keys(groupedProducts)?.map((key) => {
				let products = groupedProducts[key];
				return (
					<div className="compair-table scroll-x-label pt-4" key={`group_products_${key}`}>
						<h3 className="table-title gray_dark_01 weight-600 regular-14">
							{key} ({products?.length})
						</h3>
						{selectedViewType === 'NARROW_VIEW' ? (
							<PomCardView
								pomList={products}
								onDeletePomFromList={onDeletePomFromList}
								selectedBrand={selectedBrand}
							/>
						) : (
							<TableView
								pomList={products}
								onDeletePomFromList={onDeletePomFromList}
								selectedBrand={selectedBrand}
							/>
						)}
					</div>
				);
			});
		} else
			return selectedViewType === 'NARROW_VIEW' ? (
				<PomCardView
					pomList={allPomList?.data}
					onDeletePomFromList={onDeletePomFromList}
					selectedBrand={selectedBrand}
				/>
			) : (
				<TableView
					pomList={allPomList?.data}
					onDeletePomFromList={onDeletePomFromList}
					selectedBrand={selectedBrand}
				/>
			);
	};

	return (
		<>
			<PomListTopFilter
				onCollspseFilter={onCollspseFilter}
				selectedBrand={selectedBrand}
				setSelectedBrand={setSelectedBrand}
				onSetViewSetting={onSetViewSetting}
				onAddPom={() => setIsOpen(true)}
				totalElements={allPomList?.totalElements}
				selectedViewType={selectedViewType}
			/>
			{/* Filter Data container */}
			<section className="filter-data-container filter-v3-data-container pom-list-filter">
				<FilterComponent
					filterPageName={allFilterPages.POM_LIST}
					fields={pomListFilterFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>

				{allPomList?.data?.length > 0 && (
					<div className="data-table scroll-x-label">{renderPomList()}</div>
				)}

				{allPomList?.data?.length === 0 && (
					<div className="data-table">
						<div className="pom-empty d-flex justify-content-center align-items-center flex-column">
							<p className="medium-16 gray_dark_03">No POM found</p>
							<button className="button size32 outline" onClick={() => setIsOpen(true)}>
								<img src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`} alt />
								<span className="ml-1 regular-12 gray_dark_01">Add POM</span>
							</button>
						</div>
					</div>
				)}
				{isOpen && <AddPomInfo type="ADD" onClose={() => setIsOpen(false)} />}
			</section>
		</>
	);
};

export default PomList;
